// Cấu hình công ty (tenant settings). identity-service lưu PHẦN GHI ĐÈ; giá trị hiệu lực = mergeSettings(stored).
// Các service tiêu thụ identity.tenantSettings.updated nhận snapshot hiệu lực đầy đủ.
import { z } from 'zod';

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Mã màu dạng #RRGGBB');
const int = (min: number) => z.number().int().min(min);
const pct = z.number().min(0).max(100);
const money = z.number().int().min(0);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Giờ dạng HH:mm');

export const rulesSchema = z.object({
  lockMinutes: int(5),
  maxLocks: int(1),
  priorityMinutes: int(0),
  extendMinutes: int(0),
  approveSlaMinutes: int(0),
  slaMinutes: int(1),
  protectDays: int(1),
  autoAssign: z.enum(['roundrobin', 'manual']),
});

export const commissionSchema = z.object({
  onPreVAT: z.boolean(),
  share: z.object({ official: pct, probation: pct, ctv: pct }),
  tiers: z.array(z.object({ minDeals: int(0), bonusPct: z.number().min(0) })),
  leaderPct: z.number().min(0),
  managerPct: z.number().min(0),
  referralPct: z.number().min(0),
  kpiBonus: z.array(z.object({ minPct: z.number().min(0), amount: money })),
  tax: z.object({ official: pct, probation: pct, ctv: pct }),
  taxThreshold: money,
  insurancePct: pct,
});

export const attendanceSchema = z
  .object({
    start: hhmm,
    end: hhmm,
    graceMin: int(0),
    fullHours: z.number().min(0),
    halfHours: z.number().min(0),
    workDays: z.array(z.number().int().min(0).max(6)),
    lateFree: int(0),
    latePenalty: money,
    annualLeave: z.number().min(0),
    holidays: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
  })
  .refine((a) => a.start < a.end, { message: 'Giờ vào ca phải trước giờ tan ca', path: ['end'] })
  .refine((a) => a.halfHours <= a.fullHours, { message: 'Giờ nửa công không vượt giờ đủ công', path: ['halfHours'] });

/** Ảnh logo: URL ảnh (https) hoặc data URL ảnh đã thu nhỏ (≤ ~300 KB). */
const logoAsset = z.object({
  assetId: z.string().max(200),
  url: z
    .string()
    .max(400_000)
    .refine((u) => /^data:image\/(png|jpeg|webp|svg\+xml);base64,/.test(u) || /^https:\/\//.test(u), 'Logo phải là ảnh PNG/JPG/WEBP/SVG'),
  name: z.string().max(200).optional(),
});

export const profileSchema = z.object({
  name: z.string().trim().min(1, 'Nhập tên công ty').max(200),
  brand: hex,
  brand2: hex,
  /** Logo cho nền sáng — hiển thị cả ở màn đăng nhập. */
  logo: logoAsset.nullable(),
  /** Logo cho nền tối (bản trắng); vắng / null ⇒ chưa có, giao diện tối đặt logo thường trên tấm nền trắng. Không có trong
   *  DEFAULT_SETTINGS (giữ khớp cấu hình mặc định của prototype — packages/domain/test/exports.test.ts). */
  logoDark: logoAsset.nullable().optional(),
});

export type Rules = z.infer<typeof rulesSchema>;
export type CommissionPolicy = z.infer<typeof commissionSchema>;
export type AttendancePolicy = z.infer<typeof attendanceSchema>;
export type CompanyProfile = z.infer<typeof profileSchema>;

export interface TenantSettings {
  profile: CompanyProfile;
  rules: Rules;
  sources: string[];
  commission: CommissionPolicy;
  attendance: AttendancePolicy;
  modules: Record<string, boolean>;
}

export const DEFAULT_SOURCES = ['Facebook Ads', 'Zalo', 'Google Ads', 'Website', 'Hotline', 'Sự kiện mở bán', 'Khách giới thiệu', 'Nguồn cá nhân', 'Tiktok'];

export const DEFAULT_SETTINGS: TenantSettings = {
  profile: { name: 'Công ty của bạn', brand: '#2F89C9', brand2: '#414042', logo: null },
  rules: { lockMinutes: 120, maxLocks: 2, priorityMinutes: 15, extendMinutes: 60, approveSlaMinutes: 30, slaMinutes: 30, protectDays: 30, autoAssign: 'roundrobin' },
  sources: DEFAULT_SOURCES,
  commission: {
    onPreVAT: true,
    share: { official: 40, probation: 35, ctv: 55 },
    tiers: [
      { minDeals: 3, bonusPct: 5 },
      { minDeals: 5, bonusPct: 10 },
    ],
    leaderPct: 5,
    managerPct: 3,
    referralPct: 5,
    kpiBonus: [
      { minPct: 100, amount: 3_000_000 },
      { minPct: 150, amount: 8_000_000 },
    ],
    tax: { official: 0, probation: 0, ctv: 10 },
    taxThreshold: 2_000_000,
    insurancePct: 10.5,
  },
  attendance: { start: '08:30', end: '17:30', graceMin: 10, fullHours: 7, halfHours: 3.5, workDays: [1, 2, 3, 4, 5, 6], lateFree: 3, latePenalty: 50_000, annualLeave: 12, holidays: [] },
  modules: {},
};

export type StoredSettings = {
  profile?: Partial<CompanyProfile>;
  rules?: Partial<Rules>;
  sources?: string[];
  commission?: Partial<Omit<CommissionPolicy, 'share' | 'tax'>> & { share?: Partial<CommissionPolicy['share']>; tax?: Partial<CommissionPolicy['tax']> };
  attendance?: Partial<AttendancePolicy>;
  modules?: Record<string, boolean>;
};

/** Gộp phần ghi đè với mặc định (gộp nông từng phần, gộp sâu share/tax; sources rỗng ⇒ mặc định). */
export function mergeSettings(s: StoredSettings | null | undefined): TenantSettings {
  const c = s ?? {};
  const d = DEFAULT_SETTINGS;
  return {
    profile: { ...d.profile, ...(c.profile ?? {}) },
    rules: { ...d.rules, ...(c.rules ?? {}) },
    sources: c.sources && c.sources.length ? c.sources : d.sources,
    commission: {
      ...d.commission,
      ...(c.commission ?? {}),
      share: { ...d.commission.share, ...(c.commission?.share ?? {}) },
      tax: { ...d.commission.tax, ...(c.commission?.tax ?? {}) },
    },
    attendance: { ...d.attendance, ...(c.attendance ?? {}) },
    modules: { ...(c.modules ?? {}) },
  };
}

export const SETTINGS_SECTIONS = ['profile', 'rules', 'sources', 'commission', 'attendance', 'modules'] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

/** Dữ liệu PATCH cài đặt: mỗi phần gửi lên là bản đầy đủ của phần đó (giao diện sửa theo tab). */
export const updateSettingsInput = z.object({
  expectedVersion: z.number().int().min(1),
  profile: profileSchema.optional(),
  rules: rulesSchema.optional(),
  sources: z
    .array(z.string().trim().min(1))
    .refine((a) => new Set(a).size === a.length, 'Nguồn khách bị trùng')
    .optional(),
  commission: commissionSchema.optional(),
  attendance: attendanceSchema.optional(),
  modules: z.record(z.string(), z.boolean()).optional(),
});
export type UpdateSettingsInput = z.infer<typeof updateSettingsInput>;
